
import React, { useEffect, useRef } from 'react';
import { Input } from '@/components/ui/input';
import { Search, X, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Courier } from '@/services/types/courierTypes';
import { useCollectionForm } from '../context/CollectionFormContext';
import { ScrollArea } from '@/components/ui/scroll-area';

export const CourierSelector = () => {
  const {
    loading,
    searchQuery,
    setSearchQuery,
    selectedCourierId,
    setSelectedCourierId,
    filteredCouriers,
    displayCount,
    searchFocused,
    setSearchFocused,
    clearSelection
  } = useCollectionForm();

  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Фокус на поле ввода при монтировании
  useEffect(() => {
    if (!selectedCourierId && inputRef.current) {
      inputRef.current.focus();
    }
  }, [selectedCourierId]);

  // Обработчик клика вне компонента поиска
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        resultsRef.current && 
        !resultsRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setSearchFocused]);

  // Выбранный курьер
  const selectedCourier = selectedCourierId 
    ? filteredCouriers.find(c => c.id === selectedCourierId) 
    : null;

  // Обработчик изменения поиска
  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    if (value.length > 0) {
      setSearchFocused(true);
    }
  };

  // Обработчик очистки поиска
  const handleClearSearch = () => {
    setSearchQuery('');
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  // Обработчик выбора курьера
  const handleSelectCourier = (courier: Courier) => {
    setSelectedCourierId(courier.id);
    setSearchFocused(false);
  };

  // Показывать ли выпадающий список результатов
  const showResults = searchFocused && searchQuery.length >= 2;

  // Если выбран курьер, показываем карточку курьера
  if (selectedCourier) {
    return (
      <Card className="border rounded-md shadow-sm">
        <CardContent className="p-4">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-base font-medium">{selectedCourier.name}</h3>
              {selectedCourier.phone && (
                <p className="text-sm text-muted-foreground">Телефон: {selectedCourier.phone}</p>
              )}
              {selectedCourier.card_last4 && (
                <p className="text-xs text-muted-foreground mt-1">
                  Карта: {selectedCourier.card_type || ''} *{selectedCourier.card_last4}
                </p>
              )}
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={clearSelection}
            >
              Изменить
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Иначе показываем поле поиска
  return (
    <div className="space-y-2" ref={containerRef}>
      <div className="relative">
        <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input 
          ref={inputRef}
          placeholder="Поиск курьеров по имени или телефону" 
          className="pl-8"
          value={searchQuery}
          onChange={(e) => handleSearchChange(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          disabled={loading}
        />
        {searchQuery && (
          <Button 
            variant="ghost" 
            size="icon" 
            className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 rounded-full"
            onClick={handleClearSearch}
            disabled={loading}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      
      <div className="flex justify-end">
        <Badge variant="outline" className="h-6 text-xs">
          {loading ? "Загрузка..." : `Найдено: ${displayCount}`}
        </Badge>
      </div>
      
      {showResults && (
        <div 
          ref={resultsRef}
          className="absolute z-50 mt-1 bg-background rounded-md border shadow-lg overflow-hidden"
          style={{ 
            width: containerRef.current ? containerRef.current.offsetWidth : 'auto',
            maxWidth: '100%',
            maxHeight: '300px'
          }}
        >
          {loading ? (
            <div className="p-4 flex justify-center">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : filteredCouriers.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              Курьеры не найдены
            </div>
          ) : (
            <ScrollArea className="max-h-[300px]">
              <div>
                {filteredCouriers.map((courier) => (
                  <div 
                    key={courier.id}
                    className="p-3 hover:bg-muted cursor-pointer border-b last:border-b-0"
                    onClick={() => handleSelectCourier(courier)}
                  >
                    <div className="font-medium">{courier.name}</div>
                    {courier.phone && (
                      <div className="text-sm text-muted-foreground">{courier.phone}</div>
                    )}
                    {courier.card_last4 && (
                      <div className="text-xs text-muted-foreground mt-1">
                        Карта: {courier.card_type || ''} *{courier.card_last4}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </div>
      )}
    </div>
  );
};
